import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Navbar } from "@/components/Navbar";
import { Logo } from "@/components/Logo";
import {
  ArrowRight, Gavel, MessageSquare, PackageCheck, ShieldCheck,
  TrendingDown, Truck, UserRoundCheck, Phone, Mail, MapPin, Search,
} from "lucide-react";

function TrackSearchBar() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const go = (e) => { e.preventDefault(); if (q.trim()) nav(`/track/${encodeURIComponent(q.toUpperCase().trim())}`); };
  return (
    <form onSubmit={go} className="flex gap-2" data-testid="landing-track-form">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Enter CN number e.g. NL-2608-00001"
          data-testid="landing-track-input"
          className="h-12 pl-10 bg-black/60 border-white/20 text-white placeholder:text-zinc-500 rounded-sm focus-visible:ring-[#FF5A00] font-mono-ll" />
      </div>
      <Button type="submit" data-testid="landing-track-btn"
        className="h-12 rounded-sm bg-white text-black hover:bg-zinc-100 font-bold px-6">Track</Button>
    </form>
  );
}

const HERO_IMG =
  "https://images.unsplash.com/photo-1774013603273-03507c48a0e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHx0cnVjayUyMG9uJTIwaGlnaHdheSUyMGluZGlhfGVufDB8fHx8MTc4NTU3ODQyM3ww&ixlib=rb-4.1.0&q=85";
const LORRY_IMG =
  "https://images.pexels.com/photos/14797991/pexels-photo-14797991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const SENDER_IMG =
  "https://images.pexels.com/photos/4487383/pexels-photo-4487383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DRIVER_IMG =
  "https://images.unsplash.com/photo-1583373834259-46cc92173cb7?auto=format&fit=crop&w=1200&q=70";

const DIRECTORS = [
  {
    name: "Dushyant Kumar",
    designation: "Director · Systems & Customer Excellence",
    state: "Rajasthan",
    initials: "DK",
    color1: "#FF5A00",
    color2: "#FFB27A",
    role: "Systems, Cost & Customer Excellence",
    bio: "Dushyant leads the system-building side of Northline — the tech platform, unit economics and the customer experience that keeps shippers coming back. He obsesses over cost per kilometre, on-time delivery percentages and every rupee saved for the client, and translates them into features the ops team actually uses on the ground.",
  },
  {
    name: "Hanuman Singh",
    designation: "Director · Operations & Fleet",
    state: "Haryana",
    initials: "HS",
    color1: "#FFD54D",
    color2: "#FF9500",
    role: "Operations, Manpower & Lorry Arrangement",
    bio: "Hanuman has spent 10 years arranging manpower and lorries across the north-Indian corridor. He runs the yard, negotiates rates with fleet owners, on-boards drivers and makes sure every load has the right vehicle at the loading bay — no matter the season, festival rush or fuel spike.",
  },
  {
    name: "Abhishek",
    designation: "Director · Analytics & Process",
    state: "Haryana",
    initials: "AB",
    color1: "#5DB0FF",
    color2: "#1E3A8A",
    role: "Logistics Analytics & Process Design",
    bio: "Abhishek is the sharp eye that finds where logistics leaks money — detention, empty return legs, mis-billed extra charges, driver idle time. He turns those gaps into dashboards, alerts and process changes so the team fixes them before they cost the customer, and keeps Northline learning every trip.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden noise" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
          <div className="absolute inset-0 asphalt-grid opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="overline text-[#FF5A00] mb-6" data-testid="hero-overline">
                LOGISTICS MARKETPLACE • BHARAT
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tight">
                Post loads.<br />
                <span className="text-[#FF5A00]">Hire drivers.</span><br />
                Move it today.
              </h1>
              <p className="mt-8 text-lg text-zinc-300 max-w-2xl leading-relaxed">
                India&apos;s no-broker transport yard. Senders publish consignments,
                verified lorry owners bid in real time, and licensed drivers get hired
                for one-way & round trips.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <div className="w-full max-w-xl mb-2">
                  <div className="overline text-white/80 mb-2">TRACK YOUR SHIPMENT</div>
                  <TrackSearchBar />
                </div>
                <Link to="/register?role=sender">
                  <Button data-testid="cta-sender-btn" size="lg"
                    className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-14 px-8">
                    Post a Load <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/register?role=lorry_owner">
                  <Button data-testid="cta-owner-btn" size="lg" variant="outline"
                    className="rounded-sm border-white/30 bg-transparent hover:bg-white/10 text-white h-14 px-8 font-semibold">
                    Register Lorry
                  </Button>
                </Link>
                <Link to="/register?role=driver">
                  <Button data-testid="cta-driver-btn" size="lg" variant="outline"
                    className="rounded-sm border-[#FFD700]/60 bg-transparent hover:bg-[#FFD700]/10 text-[#FFD700] h-14 px-8 font-semibold">
                    I&apos;m a Driver
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 grid grid-cols-2 gap-3">
              <StatTile label="Loads posted / month" value="12,480" />
              <StatTile label="Avg bid savings" value="18%" accent />
              <StatTile label="Verified lorries" value="4,300+" />
              <StatTile label="Licensed drivers" value="6,700+" />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24" data-testid="how-it-works-section">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline text-[#FF5A00]">01 — 04</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl mt-4 tracking-tight">
              How the yard <br /> actually works.
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              { icon: PackageCheck, t: "Post Load or Driver", d: "Sender lists a consignment or a driver-hire request with route, dates & budget." },
              { icon: Gavel, t: "Bids Roll In", d: "Verified lorry owners and licensed drivers place their bids." },
              { icon: MessageSquare, t: "Negotiate Live", d: "Chat or send counter-offers till both sides agree on the number." },
              { icon: Truck, t: "Confirm & Move", d: "Sender accepts winning bid. Track posted → in-transit → delivered." },
            ].map(({ icon: I, t, d }, i) => (
              <div key={i} className="p-6 bg-[#141414] border border-[#2A2A2A] hover:-translate-y-1 hover:border-[#FF5A00]/60 transition-transform">
                <I className="w-6 h-6 text-[#FF5A00]" />
                <div className="mt-4 font-display font-bold text-xl">{t}</div>
                <div className="mt-2 text-sm text-zinc-400 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR SENDERS */}
      <section id="senders" className="border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={SENDER_IMG} alt="Sender" className="w-full h-[480px] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-[#FF5A00] text-black p-6 max-w-xs">
              <div className="overline">Sender pain killed</div>
              <div className="font-display font-black text-2xl mt-2">No more calling 12 brokers.</div>
            </div>
          </div>
          <div>
            <div className="overline text-[#FF5A00]">FOR SENDERS / SHIPPERS</div>
            <h3 className="font-display font-black text-4xl sm:text-5xl mt-4 tracking-tight">
              List once. <br /> Watch quotes race down.
            </h3>
            <ul className="mt-8 space-y-4">
              <FeatureRow icon={TrendingDown} title="Reverse-auction pricing" desc="Multiple providers compete on your load — you pick the winner." />
              <FeatureRow icon={MessageSquare} title="Counter-offer chat" desc="Not happy with a bid? Send a counter-price in one click." />
              <FeatureRow icon={ShieldCheck} title="Only verified partners" desc="Every lorry & driver is registered with vehicle / license details." />
            </ul>
            <Link to="/register?role=sender">
              <Button data-testid="senders-cta-btn" className="mt-10 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-12 px-6">
                Start posting free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* HIRE A DRIVER */}
      <section id="drivers" className="border-t border-[#2A2A2A] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="overline text-[#FFD700]">NEW · HIRE A DRIVER</div>
            <h3 className="font-display font-black text-4xl sm:text-5xl mt-4 tracking-tight">
              Vehicle ready. <br /> <span className="text-[#FFD700]">Driver in 24 hours.</span>
            </h3>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Post a driver-hire request for a Truck, Bus, Car or Trailer — with origin,
              destination, commodity type and trip length. Licensed drivers with matching
              experience place their bids and you pick the right one.
            </p>
            <ul className="mt-8 space-y-4">
              <FeatureRow icon={UserRoundCheck} title="DL & experience verified" iconColor="text-[#FFD700]" desc="Every driver signs up with DL number, home town & years on the road." />
              <FeatureRow icon={Truck} title="Any vehicle class" iconColor="text-[#FFD700]" desc="Truck, Bus, Car, Trailer — pick the class you need." />
              <FeatureRow icon={Gavel} title="Bid-and-hire pricing" iconColor="text-[#FFD700]" desc="Fair driver rates set by the market, not middlemen." />
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register?role=sender">
                <Button data-testid="hire-driver-cta-btn" className="rounded-sm bg-[#FFD700] hover:bg-[#FFE24E] text-black font-bold h-12 px-6">
                  Hire a Driver <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/register?role=driver">
                <Button data-testid="driver-signup-cta-btn" variant="outline"
                  className="rounded-sm border-[#FFD700]/40 bg-transparent hover:bg-[#FFD700]/10 text-[#FFD700] h-12 px-6 font-semibold">
                  I&apos;m a Driver
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <img src={DRIVER_IMG} alt="Driver" className="w-full h-[480px] object-cover" />
            <div className="absolute -top-6 -left-6 bg-[#FFD700] text-black p-6 max-w-xs">
              <div className="overline">New income lane</div>
              <div className="font-display font-black text-2xl mt-2">Drive for hire, direct.</div>
            </div>
          </div>
        </div>
      </section>

      {/* FOR LORRY OWNERS */}
      <section id="owners" className="border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <div className="overline text-[#FF5A00]">FOR LORRY OWNERS / FLEETS</div>
            <h3 className="font-display font-black text-4xl sm:text-5xl mt-4 tracking-tight">
              Empty return trip? <br /> <span className="text-[#FF5A00]">Fill it in minutes.</span>
            </h3>
            <ul className="mt-8 space-y-4">
              <FeatureRow icon={Gavel} title="Bid on any consignment" desc="Filter loads by route, material or vehicle type — bid your number." />
              <FeatureRow icon={MessageSquare} title="Direct sender chat" desc="No middleman. Talk to the load owner and close the deal." />
              <FeatureRow icon={Truck} title="Register once, drive forever" desc="Add vehicle type, capacity & number — start bidding same day." />
            </ul>
            <Link to="/register?role=lorry_owner">
              <Button data-testid="owners-cta-btn" className="mt-10 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold h-12 px-6">
                Register your lorry <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="relative order-1 lg:order-2">
            <img src={LORRY_IMG} alt="Lorry owner" className="w-full h-[480px] object-cover" />
            <div className="absolute -top-6 -left-6 bg-[#FF5A00] text-black p-6 max-w-xs">
              <div className="overline">Fleet income up</div>
              <div className="font-display font-black text-2xl mt-2">Zero-broker pricing.</div>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="border-t border-[#2A2A2A] bg-[#0A0A0A]" id="leadership" data-testid="leadership-section">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-1">
              <div className="overline text-[#FF5A00]">BOARD · LEADERSHIP</div>
              <h2 className="font-display font-black text-4xl mt-3 leading-[1.05] tracking-tight">
                The people running<br/>
                <span className="text-[#FF5A00]">Northline.</span>
              </h2>
              <p className="mt-6 text-zinc-400 text-sm leading-relaxed max-w-md">
                Second-generation operators from Hisar and Bikaner who&apos;ve moved
                loads across the north Indian corridor for over two decades.
              </p>
              <div className="mt-8 p-5 border border-[#2A2A2A] bg-[#141414]">
                <div className="overline text-zinc-500">REGISTERED OFFICE</div>
                <div className="mt-2 text-sm text-zinc-200 leading-relaxed" data-testid="registered-address">
                  Plot No. #107 Plaza, Cloth Market,<br />
                  Nagori Gate, Rajguru Market Road,<br />
                  Hisar, Haryana 125001, India.
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 grid sm:grid-cols-1 md:grid-cols-3 gap-4">
              {DIRECTORS.map((d, i) => (
                <div key={d.name} data-testid={`director-card-${i + 1}`}
                  className="p-5 border border-[#2A2A2A] bg-[#141414] hover:border-[#FF5A00]/60 transition-colors flex flex-col">
                  <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center font-display font-black text-2xl shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${d.color1} 0%, ${d.color2} 100%)` }}>
                    <span className="text-black">{d.initials}</span>
                  </div>
                  <div className="mt-4 text-center">
                    <div className="font-display font-black text-lg">{d.name}</div>
                    <div className="overline text-[#FF5A00] mt-1 text-[10px]">{d.designation}</div>
                    <div className="mt-2 text-[10px] text-zinc-500 uppercase tracking-widest">
                      Resident of <span className="text-zinc-300">{d.state}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#2A2A2A] flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF5A00] font-bold mb-1.5">{d.role}</div>
                    <p className="text-xs text-zinc-400 leading-relaxed" data-testid={`director-bio-${i + 1}`}>
                      {d.bio}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT / FOOTER */}
      <footer className="border-t border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <Logo />
            <div className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md">
              Northline Logistics Private Limited — a marketplace connecting shippers,
              lorry owners and licensed drivers across India. No brokers, no markups.
            </div>
            <div className="mt-6 grid gap-2">
              <ContactLine icon={Phone} label="Helpline" value="+91 99966 61953 / 99966 61194" testId="footer-phone" />
              <ContactLine icon={Mail} label="Email" value="Dushyant.kumar@northline.co.in" testId="footer-email-1" />
              <ContactLine icon={Mail} label="Email" value="Sandeep@northline.co.in" testId="footer-email-2" />
              <ContactLine icon={MapPin} label="Registered" value="#107 Plaza, Cloth Market, Nagori Gate, Rajguru Market Road, Hisar, Haryana 125001" testId="footer-registered" />
            </div>
          </div>
          <FooterCol title="Platform" items={[
            { label: "Post a Load", href: "/register?role=sender" },
            { label: "Register Lorry", href: "/register?role=lorry_owner" },
            { label: "Driver Signup", href: "/register?role=driver" },
            { label: "Login", href: "/login" },
          ]} />
          <FooterCol title="Support" items={[
            { label: "How it works", href: "/#how" },
            { label: "Senders", href: "/#senders" },
            { label: "Hire Driver", href: "/#drivers" },
            { label: "Lorry Owners", href: "/#owners" },
          ]} />
        </div>
        <div className="border-t border-[#2A2A2A] py-6">
          <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-4 items-center justify-between text-xs text-zinc-500 font-mono-ll">
            <div>© {new Date().getFullYear()} Northline Logistics Private Limited. All rights reserved.</div>
            <div className="uppercase tracking-wider">Bharat&apos;s transport bidding yard</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className={`p-5 border border-[#2A2A2A] ${accent ? "bg-[#FF5A00] text-black" : "bg-[#141414]"}`}>
      <div className={`overline ${accent ? "text-black/70" : "text-zinc-500"}`}>{label}</div>
      <div className="font-display font-black text-3xl mt-2 tracking-tight">{value}</div>
    </div>
  );
}

function FeatureRow({ icon: I, title, desc, iconColor = "text-[#FF5A00]" }) {
  return (
    <li className="flex gap-4">
      <div className={`shrink-0 w-10 h-10 border border-[#2A2A2A] flex items-center justify-center ${iconColor}`}>
        <I className="w-5 h-5" />
      </div>
      <div>
        <div className="font-display font-bold text-lg">{title}</div>
        <div className="text-sm text-zinc-400 mt-1">{desc}</div>
      </div>
    </li>
  );
}

function ContactLine({ icon: I, label, value, testId }) {
  return (
    <div className="flex items-start gap-3 text-sm" data-testid={testId}>
      <I className="w-4 h-4 text-[#FF5A00] mt-0.5" />
      <div>
        <div className="overline text-zinc-500">{label}</div>
        <div className="text-zinc-200">{value}</div>
      </div>
    </div>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <div className="overline text-[#FF5A00]">{title}</div>
      <ul className="mt-4 space-y-2 text-sm">
        {items.map((i) => (
          <li key={i.label}>
            <a href={i.href} className="text-zinc-300 hover:text-white">{i.label}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}
